import React, { useEffect, useState } from "react";
import { CeckpointStepV2, CehckpointFieldsV2 } from "src/types/checkpoint";
import { Button, Checkbox } from "@rio-cloud/rio-uikit";
import FilePicker from "@rio-cloud/rio-uikit/FilePicker";
import ConfirmDialog from "../Dialogs/Confirm";
import { minutesToHHMM } from "src/utils/time2minutes";
import { forceHHMM, formatMaskTime } from "@utils";

const MAX_FILES = 5;
const FILE_ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/plain": [".txt"],
};

const isImageFile = (entry: FileEntry): boolean => {
  return entry.file?.type?.startsWith("image/") ?? false;
};

const getFileIcon = (entry: FileEntry): string => {
  const name = entry.file?.name?.toLowerCase() || "";
  if (name.endsWith(".pdf")) return "rioglyph rioglyph-file-pdf";
  if (name.endsWith(".docx") || name.endsWith(".doc")) return "rioglyph rioglyph-file-doc";
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) return "rioglyph rioglyph-file-xls";
  if (name.endsWith(".txt")) return "rioglyph rioglyph-file-text";
  return "rioglyph rioglyph-file";
};

const isDocumentField = (f: CehckpointFieldsV2): boolean => {
  return f.field?.name === "DOCUMENT" || f.field?.id === 19;
};

const hasDocumentField = (step: CeckpointStepV2): boolean => {
  return step.fields?.some(isDocumentField) ?? false;
};

const getFieldTypeString = (type: any): string | undefined => {
  if (!type) return undefined;
  if (typeof type === "string") return type;
  if (typeof type === "object" && type.title) {
    const map: Record<string, string> = {
      "Imagem": "IMAGE",
      "Número": "NUMBER",
      "Texto": "TEXT",
      "Data": "DATE",
      "Lista": "LIST",
      "Booleano": "BOOLEAN",
    };
    return map[type.title] || type.title;
  }
  return undefined;
};

const getDocumentActualValues = (step: CeckpointStepV2): string[] => {
  const docField = step.fields?.find(isDocumentField);
  if (!docField) return [];
  if (docField.actualValues && Array.isArray(docField.actualValues) && docField.actualValues.length > 0) {
    return docField.actualValues;
  }
  if (docField.actualValue) {
    return [String(docField.actualValue)];
  }
  return [];
};

const getFileNameFromS3 = (s3Path: string): string => {
  const parts = s3Path.split("/");
  return parts[parts.length - 1] || "arquivo";
};

const getFileIconByName = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "rioglyph rioglyph-file-pdf";
  if (lower.endsWith(".docx") || lower.endsWith(".doc")) return "rioglyph rioglyph-file-doc";
  if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "rioglyph rioglyph-file-xls";
  if (lower.endsWith(".txt")) return "rioglyph rioglyph-file-text";
  if (lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "rioglyph rioglyph-file-image";
  return "rioglyph rioglyph-file";
};

export interface ExistingDocument {
  s3Path: string;
  fileName: string;
}

export type FileEntry = { file: File; preview: string };

interface Props {
  steps: CeckpointStepV2[];
  deleteStep: (stepId: number) => void;
  updateStep: React.Dispatch<React.SetStateAction<CeckpointStepV2[]>>;
  onFilesChange?: (files: Record<string, FileEntry[]>) => void;
}

export const StepRender = ({ steps, deleteStep, updateStep, onFilesChange }: Props) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const [stepFiles, setStepFiles] = useState<Record<string, FileEntry[]>>({});
  const [existingDocs, setExistingDocs] = useState<Record<string, ExistingDocument[]>>({});

  const getDocumentFieldIndex = (step: CeckpointStepV2): number => {
    return step.fields?.findIndex(isDocumentField) ?? -1;
  };

  useEffect(() => {
    steps.forEach((step, i) => {
      const actualValues = getDocumentActualValues(step);
      if (actualValues.length === 0) return;

      const docs: ExistingDocument[] = actualValues.map((path) => ({
        s3Path: path,
        fileName: getFileNameFromS3(path),
      }));
      setExistingDocs((prev) => ({ ...prev, [String(i)]: docs }));
    });
  }, [steps]);

  const handlePickFiles = async (stepIndex: number, fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const key = String(stepIndex);
    const current = stepFiles[key] || [];
    const remaining = MAX_FILES - current.length;
    if (remaining <= 0) return;

    const picked = Array.from(fileList).slice(0, remaining);
    const newEntries: FileEntry[] = picked.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
    }));

    const updated = [...current, ...newEntries];
    const newState = { ...stepFiles, [key]: updated };
    setStepFiles(newState);
    onFilesChange?.(newState);
  };

  const handleDeleteFile = (stepIndex: number, fileIndex: number) => {
    const key = String(stepIndex);
    const current = stepFiles[key] || [];
    const entry = current[fileIndex];
    if (entry.preview) URL.revokeObjectURL(entry.preview);
    const updated = current.filter((_, i) => i !== fileIndex);
    const newState = { ...stepFiles, [key]: updated };
    setStepFiles(newState);
    onFilesChange?.(newState);
  };

  const handleCheckboxChange = (step_index: number, field_index: number, checked: boolean) => {
    updateStep((prevSteps) => {
      const newSteps = [...prevSteps];
      const step = newSteps[step_index];
      if (step.fields && step.fields[field_index]) {
        const newFields = [...step.fields];
        newFields[field_index] = {
          ...newFields[field_index],
          required: checked,
        };
        step.fields = newFields;
      }
      return newSteps;
    });
  };

  const updateFieldValue = (step_index: number, field_index: number, value: string | number | boolean) => {
    updateStep((prevSteps) => {
      const newSteps = [...prevSteps];
      const step = newSteps[step_index];
      if (step.fields && step.fields[field_index]) {
        const newFields = [...step.fields];
        newFields[field_index] = {
          ...newFields[field_index],
          estimatedValue: value,
        };
        step.fields = newFields;
      }
      return newSteps;
    });
  };

  const renderField = (field: CehckpointFieldsV2, step_index: number, field_index: number) => {
    if (!field) return "N/A";
    if (isDocumentField(field)) return null;
    const fieldType = getFieldTypeString(field.field?.type);
    switch (fieldType) {
      case "NUMBER":
        return (
          <div className="">
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required === true}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field?.title}
              </label>
            </div>
            <input
              type="number"
              className="form-control"
              id="time"
              placeholder={field.field?.description || "Valor estimado"}
              value={Number(field.estimatedValue) || ""}
              onChange={(e) => {
                updateFieldValue(step_index, field_index, parseFloat(e.target.value));
              }}
              required={field.required}
            />
          </div>
        );
      case "TEXT":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required === true}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field?.title}
              </label>
            </div>
            <input
              type="text"
              className="form-control"
              id={field.id}
              placeholder={field.field?.description || ""}
              value={field.estimatedValue == null ? "" : String(field.estimatedValue)}
              onChange={(e) => updateFieldValue(step_index, field_index, e.target.value)}
              required={field.required}
            />
          </div>
        );
      case "DATE":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required === true}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field?.title ?? ""}
              </label>
            </div>
            <input
              type="date"
              className="form-control"
              id={field.id}
              placeholder={field.field?.description || ""}
              value={String(field.estimatedValue) || ""}
              onChange={(e) => updateFieldValue(step_index, field_index, e.target.value)}
              required={field.required}
            />
          </div>
        );
      case "IMAGE":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required === true}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field?.title}
              </label>
            </div>
            <div
              className="form-control"
              style={{
                border: "1px dashed #ccc",
                backgroundColor: "#f5f5f5",
                color: "#999",
                cursor: "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span className="rioglyph rioglyph-camera" />
              <span className="text-size-12">{field.field?.description || "Envio de imagem"}</span>
            </div>
          </div>
        );
      case "LIST":
        return (
          <div>
            <div className="display-flex flex-row gap-4 align-items-center">
              <Checkbox
                checked={field.required === true}
                onChange={(e) => handleCheckboxChange(step_index, field_index, e.target.checked)}
              ></Checkbox>
              <label form="time" style={{ margin: 0, verticalAlign: "middle" }}>
                {field.field?.title}
              </label>
            </div>
            <select
              className="form-control"
              id={field.id}
              value={String(field.estimatedValue) || ""}
              onChange={(e) => updateFieldValue(step_index, field_index, e.target.value)}
              required={field.required}
            >
              <option value="" disabled>
                {field.field?.description || "Selecione uma opção"}
              </option>
              {field.field?.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      default:
        return <></>;
    }
  };

  const handleDeleteStep = (i: number) => {
    setShowConfirmDialog(true);
    setStepToDelete(i);
  };

  return (
    <div className="lead" style={{ padding: "5px" }}>
      <div className="display-flex flex-column gap-10">
        {steps.map((step, step_index) => {
          let estimedTime;
          if (typeof step.estimatedTimeMinutes === "number") {
            estimedTime = minutesToHHMM(parseInt(step.estimatedTimeMinutes));
          } else {
            estimedTime = step.estimatedTimeMinutes;
          }
          const files = stepFiles[String(step_index)] || [];
          const hasDocument = hasDocumentField(step);
          const docFieldIndex = getDocumentFieldIndex(step);
          const docField = docFieldIndex >= 0 ? step.fields?.[docFieldIndex] : undefined;
          const stepExistingDocs = existingDocs[String(step_index)] || [];
          return (
            <div
              key={step.id || step_index}
              style={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}
              className="display-flex flex-column gap-5 padding-10 margin-bottom-10"
            >
              <div className="display-flex flex-row justify-content-between align-items-center">
                <div className="text-size-h6 text-bold">{`${step_index + 1}° Etapa`}</div>
                <Button
                  bsStyle={Button.DEFAULT}
                  variant={Button.VARIANT_LINK}
                  iconOnly
                  iconName='rioglyph rioglyph-trash'
                  onClick={() => {
                    handleDeleteStep(step_index);
                  }} />
              </div>
              <div
                className="fields-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                  alignItems: "center",
                }}
              >
                <div>
                  <label>Título da etapa</label>
                  <input
                    type="text"
                    className="form-control"
                    id={step.title}
                    value={step.title}
                    onChange={(e) =>
                      updateStep((prevSteps) => {
                        const newSteps = [...prevSteps];
                        newSteps[step_index].title = e.target.value;
                        return newSteps;
                      })
                    }
                  />
                </div>

                <div>
                  <label>Tempo estimado</label>

                  <input
                    type="text"
                    className="form-control"
                    min="00:00"
                    placeholder="00:00"
                    id={step.estimatedTimeMinutes}
                    value={estimedTime}
                    onChange={(e) =>
                      updateStep((prevSteps) => {
                        const newSteps = [...prevSteps];
                        newSteps[step_index].estimatedTimeMinutes = formatMaskTime(e.target.value);
                        return newSteps;
                      })
                    }
                    onBlur={(event) => {
                      updateStep((prevSteps) => {
                        const newSteps = [...prevSteps];
                        newSteps[step_index].estimatedTimeMinutes = forceHHMM(event.target.value);
                        return newSteps;
                      });
                    }}
                  />
                </div>

                {step.fields &&
                  step.fields.length > 0 &&
                  step.fields.map((field, i) => {
                    const rendered = renderField(field, step_index, i);
                    if (!rendered) return null;
                    return (
                      <div key={field.id || `field-${i}`} className="mb-10">
                        {rendered}
                      </div>
                    );
                  })}
              </div>

              {hasDocument && (() => {
                const totalCount = stepExistingDocs.length + files.length;
                return (
                  <>
                    <hr style={{ margin: "15px 0" }} />
                    <div>
                      <div className="display-flex flex-row align-items-center gap-10 margin-bottom-10">
                        <Checkbox
                          checked={docField?.required === true}
                          onChange={(e) => {
                            if (docFieldIndex >= 0) {
                              handleCheckboxChange(step_index, docFieldIndex, e.target.checked);
                            }
                          }}
                        >
                          <span className="text-bold">Envio de anexos</span>
                        </Checkbox>
                      </div>

                      <div className="display-flex flex-row align-items-center justify-content-between margin-bottom-10">
                        <label className="text-bold" style={{ margin: 0 }}>
                          Anexos ({totalCount}/{MAX_FILES})
                        </label>
                      </div>

                      {stepExistingDocs.length > 0 && (
                        <div className="display-flex flex-row gap-10 margin-bottom-10 flex-wrap">
                          {stepExistingDocs.map((doc, idx) => (
                            <div
                              key={`existing-${idx}`}
                              className="panel panel-default position-relative"
                              style={{ width: "100px", minWidth: "100px" }}
                            >
                              <div className="panel-body padding-5">
                                <div
                                  className="display-flex flex-column align-items-center justify-content-center text-center"
                                  style={{ width: "100%", aspectRatio: "1" }}
                                >
                                  <span className={`${getFileIconByName(doc.fileName)} text-size-h3 text-color-gray`} />
                                  <span
                                    className="text-size-10 text-color-dark margin-top-5 text-bold"
                                    style={{ wordBreak: "break-all", lineHeight: "1.2" }}
                                  >
                                    {doc.fileName}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {totalCount < MAX_FILES && (
                        <FilePicker
                          displayMode="full"
                          label="Selecionar arquivos"
                          className="margin-bottom-10"
                          multiple
                          onPick={(picked: FileList | null) => handlePickFiles(step_index, picked)}
                          accept={FILE_ACCEPT}
                        />
                      )}

                      {files.length > 0 && (
                        <div className="display-flex flex-row gap-10 margin-top-10 flex-wrap">
                          {files.map((entry, idx) => (
                            <div
                              key={`new-${idx}`}
                              className="panel panel-default position-relative"
                              style={{ width: "100px", minWidth: "100px" }}
                            >
                              <div
                                onClick={() => handleDeleteFile(step_index, idx)}
                                className={
                                  "width-20 aspect-ratio-1 rounded-circle bg-white border border-color-light " +
                                  "display-grid place-items-center cursor-pointer position-absolute top-0 right-0 " +
                                  "translate-x50pct-y-50pct"
                                }
                              >
                                <span className="rioglyph rioglyph-remove text-size-12" />
                              </div>
                              <div className="panel-body padding-5">
                                {isImageFile(entry) ? (
                                  <img
                                    src={entry.preview}
                                    alt={entry.file.name}
                                    style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "4px" }}
                                  />
                                ) : (
                                  <div
                                    className="display-flex flex-column align-items-center justify-content-center text-center"
                                    style={{ width: "100%", aspectRatio: "1" }}
                                  >
                                    <span className={`${getFileIcon(entry)} text-size-h3 text-color-gray`} />
                                    <span
                                      className="text-size-10 text-color-dark margin-top-5 text-bold"
                                      style={{ wordBreak: "break-all", lineHeight: "1.2" }}
                                    >
                                      {entry.file.name}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          );
        })}
      </div>
      <ConfirmDialog
        show={showConfirmDialog}
        onClickConfirm={() => {
          setShowConfirmDialog(false);
          deleteStep(stepToDelete!);
        }}
        onClickCancel={() => {
          setShowConfirmDialog(false);
          setStepToDelete(null);
        }}
        title="Excluir Etapa"
        content="Você tem certeza que deseja excluir esta etapa? Esta ação não pode ser desfeita."
        confirmButtonText="Sim, excluir"
        cancelButtonText="Não, manter"
      />
    </div>
  );
};
